import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Check, ExternalLink, LogOut, Trash2, Upload, User as UserIcon } from 'lucide-react';
import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import {
  DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID,
  EMAIL_SUMMARY_ALWAYS_INCLUDED_SCENARIO_IDS,
} from '@/Config/scenarioIds';
import { User } from '@/Domain/api/User/dto/User.dto';
import { LanguageSwitcher } from '@/Domain/components/App/LanguageSwitch/LanguageSwitch';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import ImageInput from '@/Domain/components/Shared/ImageInput/ImageInput';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { ThemeSelectDropdown } from '@/Domain/components/Shared/ThemeSelect/ThemeSelectDropdown';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { useUserProfile } from '@/Domain/hooks/User/useUserProfile/useUserProfile.hook';
import { useUserAvatar } from '@/Domain/hooks/User/userUserAvatar/useUserAvatar.hook';
import { cn } from '@/Domain/utils/cn';
import { EmailSummariesPanel } from '@/UI/User/EmailSummaries/EmailSummariesPanel';

type Props = {
  user: User;
};

const ROW_CLASSES = 'grid grid-cols-1 sm:grid-cols-2 sm:items-center gap-y-2 sm:gap-x-6';
const ROW_CLASSES_TWO_COL = 'grid grid-cols-2 items-center gap-x-6';
// 70/30 split on mobile so a lone toggle or small button doesn't sit in the middle of a half-empty cell.
const ROW_CLASSES_NARROW_RIGHT = 'grid grid-cols-[7fr_3fr] items-center gap-x-6 sm:grid-cols-2';

const RESOURCES = [
  { key: 'documentation', url: 'https://docs.quickchat.ai' },
  { key: 'blog', url: 'https://www.quickchat.ai/blog' },
  { key: 'legal_documents', url: 'https://www.quickchat.ai/legal' },
] as const;

export const UserProfilePage = ({ user }: Props) => {
  const { t } = useTranslation(['user_profile', 'sidebar_menu']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [justDeleted, setJustDeleted] = useState(false);
  const deleteFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { form, handleUpdate } = useUserProfile(user);
  const { handleUpload, handleDelete, handleError } = useUserAvatar();
  const { handleLogout } = useLogout();
  const { hash } = useLocation();

  // Mirror the send-side eligibility in quickchat/activity_summary/send.py: every non-custom tier is emailed; custom tier only when allowlisted.
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const currentScenarioId = aiAssistants?.current_ai_assistant?.scenario_id;
  const currentTier = aiAssistants?.all_ai_assistants.find(
    (assistant) => assistant.scenario_id === currentScenarioId,
  )?.pricing_tier;
  const emailSummariesVisible =
    !!currentScenarioId &&
    (EMAIL_SUMMARY_ALWAYS_INCLUDED_SCENARIO_IDS.includes(currentScenarioId) ||
      (currentTier !== undefined && currentTier !== TierEnum.CUSTOM) ||
      (process.env.NODE_ENV === 'development' &&
        currentScenarioId === DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID));

  // Browser-native hash scrolling fires before the gated panel mounts; re-do it once the gate resolves.
  const emailSummariesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!emailSummariesVisible) return;
    if (hash !== '#email-summaries') return;
    emailSummariesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [emailSummariesVisible, hash]);

  useEffect(
    () => () => {
      if (deleteFeedbackTimer.current) clearTimeout(deleteFeedbackTimer.current);
    },
    [],
  );

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteClick = async (event: ReactMouseEvent<HTMLButtonElement>) => {
    try {
      await handleDelete(event);
    } catch {
      return;
    }
    setJustDeleted(true);
    if (deleteFeedbackTimer.current) clearTimeout(deleteFeedbackTimer.current);
    deleteFeedbackTimer.current = setTimeout(() => setJustDeleted(false), 1500);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-6">
          <div>
            <CardTitle>{t('title')}</CardTitle>
            <CardDescription className="mt-1.5">{t('subtitle')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className={ROW_CLASSES_TWO_COL}>
            <div>
              <Label className="font-medium">{t('form.profile_picture')}</Label>
              <p className="text-sm text-foreground-subtle mt-1">
                {t('form.profile_picture_limitation')}
              </p>
            </div>
            <div className="flex items-center justify-end">
              <div className="relative group w-20 h-20 shrink-0">
                <Avatar className="w-20 h-20 border !border-border">
                  <AvatarImage src={user.avatar_url || ''} />
                  <AvatarFallback className="!border-border">
                    <UserIcon className="h-6 w-6 text-foreground-subtle" />
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={handleUploadClick}
                  className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer focus-visible:opacity-100 focus:outline-none"
                  aria-label={t('button.upload_new_picture')}
                >
                  <Upload className="h-6 w-6 text-white" />
                </button>
                {(user.avatar_url || justDeleted) && (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className={cn(
                      'absolute -top-1 -left-1 z-10 size-6 rounded-full text-destructive-foreground border border-background flex items-center justify-center transition-[opacity,background-color] cursor-pointer focus-visible:opacity-100 focus:outline-none',
                      justDeleted
                        ? 'opacity-100 bg-success text-success-foreground hover:bg-success'
                        : 'opacity-0 group-hover:opacity-100 bg-destructive hover:bg-destructive/90',
                    )}
                    aria-label={t('button.delete')}
                  >
                    {justDeleted ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
              </div>
              <ImageInput
                ref={fileInputRef}
                onChange={handleUpload}
                onError={handleError}
                accept="image/png, image/jpeg"
                className="hidden"
              />
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpdate)} className="space-y-6">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem className={cn(ROW_CLASSES, 'space-y-0')}>
                    <FormLabel className="font-medium">{t('form.first_name')}</FormLabel>
                    <div>
                      <FormControl>
                        <Input placeholder={t('form.first_name_placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem className={cn(ROW_CLASSES, 'space-y-0')}>
                    <FormLabel className="font-medium">{t('form.last_name')}</FormLabel>
                    <div>
                      <FormControl>
                        <Input placeholder={t('form.last_name_placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>

          <div className={ROW_CLASSES_TWO_COL}>
            <Label className="font-medium">{t('form.contact_email')}</Label>
            <span className="py-2.5 text-sm text-foreground font-medium justify-self-end">
              {user.email}
            </span>
          </div>

          {user.created_at && (
            <div className={ROW_CLASSES_TWO_COL}>
              <Label className="font-medium">{t('form.account_created')}</Label>
              <span className="py-2.5 text-sm text-foreground font-medium justify-self-end">
                {format(parseISO(user.created_at), 'd MMM yyyy')}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-6">
          <div>
            <CardTitle>{t('preferences.title')}</CardTitle>
            <CardDescription className="mt-1.5">{t('preferences.description')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className={ROW_CLASSES}>
            <Label className="font-medium">{t('form.language')}</Label>
            <LanguageSwitcher />
          </div>
          <div className={ROW_CLASSES}>
            <Label className="font-medium">{t('form.color_mode')}</Label>
            <ThemeSelectDropdown />
          </div>
          {emailSummariesVisible && (
            <div id="email-summaries" ref={emailSummariesRef} className="space-y-6">
              <EmailSummariesPanel />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-6">
          <div>
            <CardTitle>{t('resources.title')}</CardTitle>
            <CardDescription className="mt-1.5">{t('resources.description')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {RESOURCES.map(({ key, url }) => (
            <div key={key} className={ROW_CLASSES_NARROW_RIGHT}>
              <Label className="font-medium">{t(`resources.${key}`)}</Label>
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                >
                  {t('resources.open')}
                  <ExternalLink />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex">
        <Button variant="secondary" onClick={handleLogout}>
          <LogOut />
          {t('sidebar_menu:menu.account.logout')}
        </Button>
      </div>
    </div>
  );
};
